import type { StateCreator } from 'zustand';

import { getWardrobeCategoriesByGender } from '@/constants/generated';
import { isContextAvailable } from '@/miniappContext';
import { uploadBlobToCdn } from '@/services/cdnService';
import { registerCharacterPersist, saveCharacterToDrive, saveLibraryToDrive } from '@/services/driveService';
import { mergeUserItemsIntoCategories } from '@/services/userItemsStorage';
import type { RetouchPreset } from '@/types';

import { getUserUploadedItems } from './helpers';
import type { IWardrobeStore } from './types';

export const initialCharacter: IWardrobeStore['character'] = {
  imageUrl: null,
  imageFile: null,
  imageAspectRatio: null,
  gender: 'female' as const,
  selectedStyle: null,
  selectedPose: null,
  selectedBackground: null,
  selectedHairstyle: null,
  selectedHairColor: 'Natural' as const,
  isIdentityLocked: true,
  selectedRetouchPresets: {} as Record<string, RetouchPreset[]>,
};

/**
 * Character slice — character image, gender, and preset selections.
 *
 * Cross-slice writes intentionally kept here because the trigger is on the
 * character:
 * - setCharacterImage clears generation state and reaches into library via
 *   get() to register the character and persist it to Drive.
 * - clearCharacterImage clears generation state.
 * - setGender resets character selections, rebuilds wardrobe categories for
 *   the new gender, and updates the active library character's gender.
 */
export type CharacterSliceContribution = Pick<
  IWardrobeStore,
  | 'character'
  | 'setCharacterImage'
  | 'setCharacterAspectRatio'
  | 'clearCharacterImage'
  | 'setGender'
  | 'setStyle'
  | 'setPose'
  | 'setBackground'
  | 'setHairstyle'
  | 'toggleRetouchPreset'
  | 'clearRetouchPresets'
  | 'setHairColor'
  | 'toggleIdentityLock'
>;

export const createCharacterSlice: StateCreator<IWardrobeStore, [], [], CharacterSliceContribution> = (set, get) => ({
  character: initialCharacter,

  setCharacterImage: (url, file) => {
    set(s => ({
      character: { ...s.character, imageUrl: url, imageFile: file, imageAspectRatio: null },
      generation: {
        ...s.generation,
        currentResult: null,
        variants: [],
        error: null,
        viewedHistoryItem: null,
      },
    }));

    if (!url) return;

    // Add character to library immediately (blob URLs display within the session)
    const charId = get().addCharacterToLibrary(url, get().character.gender);

    if (!isContextAvailable()) return;
    // Persist the character to Drive — folder + image + hidden JSON. Registered
    // as a single in-flight promise so a concurrent Generate (PreviewPanel) can
    // await this exact save instead of racing and creating a second subfolder.
    const persistPromise = (async (): Promise<{ uid: string; name: string } | null> => {
      let persistableUrl = url;
      if (url.startsWith('blob:') && file) {
        try {
          const cdnUrl = await uploadBlobToCdn(file);
          get().updateCharacterImageUrl(charId, cdnUrl);
          persistableUrl = cdnUrl;
        } catch (err) {
          console.error('[Store] CDN upload failed for character image — blob URL will not persist across sessions:', err);
          return null;
        }
      }
      const character = get().library.characters.find(c => c.id === charId);
      if (!character || character.driveSubfolderUid) return null;
      try {
        const folder = await saveCharacterToDrive({ ...character, characterImageUrl: persistableUrl });
        if (folder) get().setCharacterDriveSubfolder(charId, folder.uid, folder.name);
        return folder;
      } catch (err) {
        console.error('[Store] saveCharacterToDrive failed:', err);
        return null;
      }
    })();

    registerCharacterPersist(charId, persistPromise);
  },

  setCharacterAspectRatio: ratio => set(s => ({ character: { ...s.character, imageAspectRatio: ratio } })),

  clearCharacterImage: () =>
    set(s => ({
      character: { ...s.character, imageUrl: null, imageFile: null, imageAspectRatio: null },
      generation: {
        ...s.generation,
        currentResult: null,
        variants: [],
        error: null,
      },
    })),

  setGender: gender => {
    const presetCategories = getWardrobeCategoriesByGender(gender);
    const currentUserItems = getUserUploadedItems(get().wardrobe.categories);
    set(s => {
      const activeId = s.library.activeCharacterId;
      const newCharacters = activeId ? s.library.characters.map(c => (c.id === activeId ? { ...c, gender } : c)) : s.library.characters;
      if (activeId) saveLibraryToDrive(newCharacters);
      return {
        character: {
          ...s.character,
          gender,
          selectedStyle: null,
          selectedPose: null,
          selectedBackground: null,
          selectedHairstyle: null,
          selectedRetouchPresets: {},
        },
        wardrobe: {
          ...s.wardrobe,
          categories: mergeUserItemsIntoCategories(presetCategories, currentUserItems),
          selectedItems: [],
        },
        library: { ...s.library, characters: newCharacters },
      };
    });
  },

  setStyle: style => set(s => ({ character: { ...s.character, selectedStyle: style } })),
  setPose: pose => set(s => ({ character: { ...s.character, selectedPose: pose } })),
  setBackground: bg => set(s => ({ character: { ...s.character, selectedBackground: bg } })),
  setHairstyle: style => set(s => ({ character: { ...s.character, selectedHairstyle: style } })),

  toggleRetouchPreset: (categoryId, preset, applyMode) =>
    set(s => {
      const current = s.character.selectedRetouchPresets[categoryId] ?? [];
      const isSelected = current.some(p => p.id === preset.id);
      let next: RetouchPreset[];
      if (isSelected) {
        next = current.filter(p => p.id !== preset.id);
      } else if (applyMode === 'stack') {
        next = [...current, preset];
      } else {
        next = [preset];
      }
      const presets = { ...s.character.selectedRetouchPresets };
      if (next.length === 0) {
        delete presets[categoryId];
      } else {
        presets[categoryId] = next;
      }
      return { character: { ...s.character, selectedRetouchPresets: presets } };
    }),

  clearRetouchPresets: (categoryId?: string) =>
    set(s => {
      if (!categoryId) {
        return { character: { ...s.character, selectedRetouchPresets: {} } };
      }
      const next = { ...s.character.selectedRetouchPresets };
      delete next[categoryId];
      return { character: { ...s.character, selectedRetouchPresets: next } };
    }),

  setHairColor: color => set(s => ({ character: { ...s.character, selectedHairColor: color } })),
  toggleIdentityLock: () => set(s => ({ character: { ...s.character, isIdentityLocked: !s.character.isIdentityLocked } })),
});
